import { useEffect, useRef } from 'react'
import { useSelector } from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'

import Canvas from './Canvas'
import Header from './Header'
import Sidebar from './Sidebar'
import SubSidebar from './SubSidebar'
import Timeline from './Timeline'
import * as Styled from './styled'
import { useInitialFetch } from 'hooks/useInitialFetch'
import { useExternalMedia } from 'hooks/useExternalMedia'
import { projectSelectors } from 'store/features/project/selectors'
import { SlideUp } from 'common-components'
import { editorSelectors } from 'store/features/editor/selectors'
import { editorThunks } from 'store/features/editor/thunks'
import { useResponsive } from 'hooks/useResponsive'
import { SMALL_DESKTOP_SIZE_BREAKPOINT } from 'consts'
import { SUB_SIDEBAR_KEYS } from 'consts/sidebar'
import { GlobalLoading } from 'components/GlobalLoading'
import EditorDndContext from 'context/EditorDndContext'
import { dispatch } from 'store'
import {
  useFeatureCredits,
  useTtsDataSync,
  useUserSounds,
  useAIImageReference,
  useImageToVideoAiLandingData,
  useOpenMobileAIVideoSidebar,
  useRemoveOldLoadingProgressData,
  useFetchLibraryFonts
} from './hooks'
import { useAppSockets } from 'hooks/useAppSockets'
import { aiImageSelectors } from 'store/features/aiImage/selectors'
import { aiImageThunks } from 'store/features/aiImage/thunks'
import { aiVideoThunks } from 'store/features/aiVideo/thunks'
import { aiVideoSelectors } from 'store/features/aiVideo/selectors'
import { userSelectors } from 'store/features/user/selectors'
import { aiLibImageThunks } from 'store/features/aiLibImage/thunks'
import { aiLibThunks } from 'store/features/aiLibVideo/thunks'
import { useVEditorInstructions } from 'hooks/useVEditorInstructions'

const Layout = () => {
  const navigate = useNavigate()
  const previousTabRef = useRef<string | null>(null)

  const closeMobileSubSidebar = () => {
    dispatch(editorThunks.setIsMobileSubSidebarOpen(false))
  }

  const { projectId: projectIdFromRoute = '' } = useParams()
  const projectIdFromStore = useSelector(projectSelectors.id)
  const hasImageStyles = useSelector(aiImageSelectors.hasStyles)
  const isProjectLoaded = useSelector(projectSelectors.isLoaded)
  const hasImageModels = useSelector(aiImageSelectors.hasModels)
  const hasVideoModels = useSelector(aiVideoSelectors.hasVideoModels)
  const isUserLoggedIn = useSelector(userSelectors.isUserLoggedIn)
  const activeSidebarTab = useSelector(editorSelectors.activeSidebarTab)
  useAppSockets()

  useExternalMedia()

  useEffect(() => {
    if (projectIdFromStore && `${projectIdFromStore}` !== projectIdFromRoute) {
      navigate(`/${projectIdFromStore}`, { replace: true })
    }
  }, [navigate, projectIdFromStore, projectIdFromRoute])

  useInitialFetch()

  useEffect(() => {
    if (!hasImageStyles && projectIdFromStore) {
      dispatch(aiImageThunks.getStyles())
      dispatch(aiImageThunks.getRestylingStyles())
    }
  }, [hasImageStyles, projectIdFromStore])

  // we need to get models here to cover mobile case ( smart edit mode)

  useEffect(() => {
    if (isProjectLoaded) {
      if (!hasImageModels) {
        dispatch(aiImageThunks.getModels())
      }
      // Wait for user data — isFreeUser falls back to true until hdMax is
      // known, which would otherwise clamp the default quality to free tier.
      if (!hasVideoModels && isUserLoggedIn) {
        dispatch(aiVideoThunks.getAIVideoModels())
      }
    }
  }, [isProjectLoaded, hasImageModels, hasVideoModels, isUserLoggedIn])

  useUserSounds()
  useFeatureCredits()
  useAIImageReference()
  useImageToVideoAiLandingData()
  useOpenMobileAIVideoSidebar()
  useTtsDataSync()
  useRemoveOldLoadingProgressData()
  useFetchLibraryFonts()
  useVEditorInstructions()

  // Reset AI Library filters when switching away from AI_LIB tab
  useEffect(() => {
    if (
      previousTabRef.current &&
      previousTabRef.current === SUB_SIDEBAR_KEYS.AI_LIB
    ) {
      if (activeSidebarTab !== SUB_SIDEBAR_KEYS.AI_LIB) {
        // Reset filters and close filter panel for both image and video
        dispatch(aiLibImageThunks.resetFilters())
        dispatch(aiLibThunks.resetFilters())
        dispatch(aiLibImageThunks.toggleFiltersOpen())
        dispatch(aiLibThunks.toggleFiltersOpen())
      }
    }
    previousTabRef.current = activeSidebarTab
  }, [activeSidebarTab])

  const isMobileSubSidebarOpen = useSelector(
    editorSelectors.isMobileSubSidebarOpen
  )
  const isMobileInnerSliderOpen = useSelector(
    editorSelectors.isMobileInnerSliderOpen
  )
  const isMobile = useResponsive(SMALL_DESKTOP_SIZE_BREAKPOINT)
  const isRendering = useSelector(projectSelectors.isRendering)
  const isProjectLoading = useSelector(projectSelectors.isLoading)

  return (
    <EditorDndContext>
      <Styled.AppContainer>
        {isRendering || isProjectLoading ? <GlobalLoading transparent /> : null}
        <Styled.EditorLayout>
          <Sidebar />
          {!isMobile && <SubSidebar />}
          <Styled.Content>
            <Styled.MainContent>
              <Styled.CanvasArea>
                <Header />
                <Canvas />
              </Styled.CanvasArea>
            </Styled.MainContent>
            <Timeline />
            {isMobile && (
              <SlideUp
                isHidden={isMobileInnerSliderOpen}
                isOpen={isMobileSubSidebarOpen}
                onClose={closeMobileSubSidebar}
              >
                <SubSidebar />
              </SlideUp>
            )}
          </Styled.Content>
        </Styled.EditorLayout>
      </Styled.AppContainer>
    </EditorDndContext>
  )
}

export default Layout
